import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, House, Inbox, LogOut, Settings2, User, Users } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';
import ConfirmDialog from './ConfirmDialog';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dziś', icon: House, path: '/panel' },
  { key: 'requests', label: 'Prośby', icon: Inbox, path: '/panel/requests' },
  { key: 'calendar', label: 'Grafik', icon: CalendarDays, path: '/panel/calendar' },
  { key: 'clients', label: 'Klientki', icon: Users, path: '/panel/clients' },
  { key: 'services', label: 'Usługi', icon: Settings2, path: '/panel/services' },
];

export default function BottomNav({ active }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function fetchPendingCount() {
      try {
        const { data } = await api.get('/requests?status=pending');
        if (mounted) {
          setPendingCount(Array.isArray(data) ? data.length : 0);
        }
      } catch (err) {
        if (mounted) {
          setPendingCount(0);
        }
      }
    }

    fetchPendingCount();

    const intervalId = setInterval(fetchPendingCount, 10000);

    function handleVisibilityChange() {
      if (!document.hidden) {
        fetchPendingCount();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [active]);

  function handleLogout() {
    setShowLogoutConfirm(true);
  }

  function confirmLogout() {
    setShowAccountMenu(false);
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <nav className="bottom-nav">
      {NAV_ITEMS.map((item) => (
        <button
          key={item.key}
          className={`bottom-nav-item ${active === item.key ? 'bottom-nav-item--active' : ''}`}
          onClick={() => {
            setShowAccountMenu(false);
            navigate(item.path);
          }}
        >
          <span className="bottom-nav-icon-wrap">
            <span className="bottom-nav-icon"><item.icon size={18} strokeWidth={1.9} /></span>
            {item.key === 'requests' && pendingCount > 0 && (
              <span className="bottom-nav-badge" aria-label={`${pendingCount} nowych próśb`}>
                {pendingCount > 9 ? '9+' : pendingCount}
              </span>
            )}
          </span>
          <span className="bottom-nav-label">{item.label}</span>
        </button>
      ))}
      <div className={`bottom-nav-account ${showAccountMenu ? 'bottom-nav-account--open' : ''}`}>
        <button
          type="button"
          className={`bottom-nav-item bottom-nav-item--account ${showAccountMenu ? 'bottom-nav-item--active' : ''}`}
          onClick={() => setShowAccountMenu((current) => !current)}
          aria-expanded={showAccountMenu}
          aria-haspopup="menu"
        >
          <span className="bottom-nav-icon-wrap">
            <span className="bottom-nav-icon"><User size={18} strokeWidth={1.9} /></span>
          </span>
          <span className="bottom-nav-label">Profil</span>
        </button>

        {showAccountMenu && (
          <div className="bottom-nav-menu" role="menu" aria-label="Menu profilu">
            <button type="button" className="bottom-nav-menu-item" onClick={handleLogout} role="menuitem">
              <LogOut size={16} strokeWidth={1.9} />
              Wyloguj
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        title="Wylogować się?"
        description="Zostaniesz wylogowana z panelu i wrócisz do ekranu logowania."
        confirmLabel="Wyloguj"
        cancelLabel="Zostań"
        confirmTone="danger"
        onCancel={() => setShowLogoutConfirm(false)}
        onConfirm={() => {
          setShowLogoutConfirm(false);
          confirmLogout();
        }}
      />
    </nav>
  );
}
